import qs from "qs";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import { SplatProspectResponse } from "providers/Types";

const PRICE_OF_POWER_PRODUCTION_URL =
  "https://www.sunrun.com/price-of-power-ONE";
const PRICE_OF_POWER_STAGING_URL =
  "https://majstg-www.sunrun.com/price-of-power-ONE";

type PriceOfPowerProspect = Partial<
  Pick<
    SplatProspectResponse,
    | "customerState"
    | "customerCity"
    | "customerZipCode"
    | "customerStreet"
    | "latitude"
    | "longitude"
  >
>;

type PriceOfPowerQueryParams = {
  state?: string;
  city?: string;
  zip?: string;
  street?: string;
  lat?: number;
  lng?: number;
  monthlyBill?: number;
};

const getMonthlyBillQueryParam = (
  preSolarUtility?: number,
  customerState?: string
): number | undefined => {
  if (customerState?.toUpperCase() === "TX") {
    return undefined;
  }
  if (preSolarUtility == null || Number.isNaN(preSolarUtility)) {
    return undefined;
  }
  return Math.trunc(preSolarUtility);
};

const getPriceOfPowerBaseUrl = (): string => {
  const env = getEnvironment();
  return env === "production"
    ? PRICE_OF_POWER_PRODUCTION_URL
    : PRICE_OF_POWER_STAGING_URL;
};

const buildPriceOfPowerQueryParams = (
  prospect?: PriceOfPowerProspect,
  preSolarUtility?: number
): PriceOfPowerQueryParams | undefined => {
  const params: PriceOfPowerQueryParams = {};

  if (prospect?.customerState) {
    params.state = prospect.customerState;
  }
  if (prospect?.customerCity) {
    params.city = prospect.customerCity;
  }
  if (prospect?.customerZipCode) {
    params.zip = prospect.customerZipCode;
  }
  if (prospect?.customerStreet) {
    params.street = prospect.customerStreet;
  }
  if (prospect?.latitude != null) {
    params.lat = prospect.latitude;
  }
  if (prospect?.longitude != null) {
    params.lng = prospect.longitude;
  }

  const monthlyBill = getMonthlyBillQueryParam(
    preSolarUtility,
    prospect?.customerState
  );
  if (monthlyBill != null) {
    params.monthlyBill = monthlyBill;
  }

  return Object.keys(params).length > 0 ? params : undefined;
};

const buildPriceOfPowerUrl = (
  prospect?: PriceOfPowerProspect,
  preSolarUtility?: number
): string => {
  const baseUrl = getPriceOfPowerBaseUrl();
  const queryParams = buildPriceOfPowerQueryParams(prospect, preSolarUtility);

  if (!queryParams) {
    return baseUrl;
  }

  return `${baseUrl}?${qs.stringify(queryParams)}`;
};

const usePriceOfPowerUrl = (
  prospect?: PriceOfPowerProspect,
  preSolarUtility?: number
): string => buildPriceOfPowerUrl(prospect, preSolarUtility);

export {
  usePriceOfPowerUrl,
  buildPriceOfPowerUrl,
  getMonthlyBillQueryParam,
  getPriceOfPowerBaseUrl,
  PRICE_OF_POWER_PRODUCTION_URL,
  PRICE_OF_POWER_STAGING_URL,
};
export type { PriceOfPowerProspect };
